'use client';

import { useTranslations } from 'next-intl';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { ShowMoreContent } from '@/src/components/features/property/ui/ShowMoreContent';
import { getHousemateDetailsDisplay, type HousemateDetailsDisplay } from './housemateDetailsModule.utils';
import { container, section, sectionHeading, text } from './housemateDetailsModule.styles';

const ROOMMATES_HEADING_ID = 'housemate-details-roommates-heading';
const PETS_HEADING_ID = 'housemate-details-pets-heading';
const PREFERRED_HEADING_ID = 'housemate-details-preferred-heading';

type Props = {
  listing: PropertyDetailListing;
};

type ContentProps = HousemateDetailsDisplay & {
  currentHousematesLabel: string;
  currentPetsLabel: string;
  preferredNewHousemateLabel: string;
};

const HousemateDetailsContent = ({
  description,
  roommatesInfo,
  petsInfo,
  preferredNewRoommate,
  currentHousematesLabel,
  currentPetsLabel,
  preferredNewHousemateLabel,
}: ContentProps) => (
  <div className={container}>
    {description && <p className={text}>{description}</p>}
    {roommatesInfo && (
      <section className={section} aria-labelledby={ROOMMATES_HEADING_ID}>
        <h3 className={sectionHeading} id={ROOMMATES_HEADING_ID}>
          {currentHousematesLabel}
        </h3>
        <p className={text}>{roommatesInfo}</p>
      </section>
    )}
    {petsInfo && (
      <section className={section} aria-labelledby={PETS_HEADING_ID}>
        <h3 className={sectionHeading} id={PETS_HEADING_ID}>
          {currentPetsLabel}
        </h3>
        <p className={text}>{petsInfo}</p>
      </section>
    )}
    {preferredNewRoommate && (
      <section className={section} aria-labelledby={PREFERRED_HEADING_ID}>
        <h3 className={sectionHeading} id={PREFERRED_HEADING_ID}>
          {preferredNewHousemateLabel}
        </h3>
        <p className={text}>{preferredNewRoommate}</p>
      </section>
    )}
  </div>
);

export const HousemateDetailsModule = ({ listing }: Props) => {
  const t = useTranslations('hdp');
  const display = getHousemateDetailsDisplay(listing);

  if (!display.hasDetails) {
    return null;
  }

  return (
    <ShowMoreContent
      controlsId="housemate-details-module-details"
      showMoreLabel={t('showMore')}
      showLessLabel={t('showLess')}
      showMoreAriaLabel={`${t('showMore')}: ${t('housemateDetailsTitle')}`}
      showLessAriaLabel={`${t('showLess')}: ${t('housemateDetailsTitle')}`}
    >
      <HousemateDetailsContent
        {...display}
        currentHousematesLabel={t('currentHousemates')}
        currentPetsLabel={t('currentPets')}
        preferredNewHousemateLabel={t('preferredNewHousemate')}
      />
    </ShowMoreContent>
  );
};
